import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Image as ImageIcon, Menu as MenuIcon, Home, FileText, Layout, Search, Plus, Trash2, ArrowUp, ArrowDown, Upload, Palette, LayoutGrid, ChevronDown, ChevronRight as ChevronRightIcon } from "lucide-react";
import api, { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import RichTextEditor from "@/components/RichTextEditor";

const TABS = [
    { id: "brand", label: "Marka & Logo", icon: ImageIcon },
    { id: "theme", label: "Tema & Renkler", icon: Palette },
    { id: "menu", label: "Üst Menü", icon: MenuIcon },
    { id: "home_sections", label: "Anasayfa Düzeni", icon: LayoutGrid },
    { id: "home", label: "Anasayfa İçerik", icon: Home },
    { id: "promo_strip", label: "Duyuru Bandı", icon: Plus },
    { id: "hotel_tail", label: "Otel Sayfası Eki", icon: LayoutGrid },
    { id: "pages", label: "Statik Sayfalar", icon: FileText },
    { id: "footer", label: "Footer", icon: Layout },
    { id: "seo", label: "SEO & Meta", icon: Search },
    { id: "seo_tools", label: "SEO Araçları", icon: Search },
];

const uid = (p = "x") => `${p}_${Math.random().toString(36).slice(2, 8)}`;
const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ş/g, "s").replace(/ı/g, "i").replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ö/g, "o").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white";

export default function AdminSiteContentPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [tab, setTab] = useState("brand");
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [editingPage, setEditingPage] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        api.get("/admin/site-content").then((r) => setData(r.data));
    }, [user, authLoading]); // eslint-disable-line

    const save = async () => {
        setSaving(true); setMsg("");
        try {
            const r = await api.put("/admin/site-content", data);
            setData(r.data);
            setMsg("✓ Kaydedildi");
            setTimeout(() => setMsg(""), 2500);
        } catch (e) {
            setMsg(e.response?.data?.detail || "Kayıt başarısız");
        } finally {
            setSaving(false);
        }
    };

    if (!data) return <AdminLayout title="Site İçeriği"><div>Yükleniyor...</div></AdminLayout>;

    const set = (section, k, v) => setData({ ...data, [section]: { ...(data[section] || {}), [k]: v } });
    const setSection = (section, v) => setData({ ...data, [section]: v });

    return (
        <AdminLayout
            title="Site İçeriği"
            action={
                <div className="flex items-center gap-3">
                    {msg && <span className="text-xs font-semibold text-emerald-600">{msg}</span>}
                    <button data-testid="save-content" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold disabled:opacity-60">
                        <Save className="h-4 w-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                </div>
            }
        >
            {/* Tabs */}
            <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/60 mb-6 max-w-3xl">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            data-testid={`tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t.id ? "bg-white dark:bg-slate-900 text-sky-600 shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-slate-700/60"}`}
                        >
                            <Icon className="h-3.5 w-3.5" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "brand" && (
                <Section title="Marka & Logo">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Acente / Marka Adı"><input data-testid="brand-name" value={data.brand?.name || ""} onChange={(e) => set("brand", "name", e.target.value)} className={INPUT} /></Field>
                        <Field label="Tagline (slogan)"><input data-testid="brand-tagline" value={data.brand?.tagline || ""} onChange={(e) => set("brand", "tagline", e.target.value)} className={INPUT} /></Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <ImageUpload testId="logo" label="Logo (açık tema · beyaz arka plana uygun)" value={data.brand?.logo_url} onChange={(url) => set("brand", "logo_url", url)} />
                        <ImageUpload testId="logo-dark" label="Logo (koyu tema · ana sayfa hero için)" value={data.brand?.logo_dark_url} onChange={(url) => set("brand", "logo_dark_url", url)} />
                        <ImageUpload testId="favicon" label="Favicon (PNG, kare)" value={data.brand?.favicon_url} onChange={(url) => set("brand", "favicon_url", url)} />
                    </div>
                </Section>
            )}

            {tab === "theme" && (
                <ThemeTab data={data} set={set} />
            )}

            {tab === "home_sections" && (
                <HomeSectionsTab data={data} setSection={setSection} />
            )}

            {tab === "promo_strip" && (
                <PromoStripTab data={data} set={set} />
            )}

            {tab === "hotel_tail" && (
                <HotelTailBlocksTab data={data} setSection={setSection} />
            )}

            {tab === "menu" && (
                <MultiLevelMenuTab data={data} setSection={setSection} />
            )}

            {tab === "home" && (
                <Section title="Anasayfa">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Hero Başlık"><input data-testid="hero-title" value={data.home?.hero_title || ""} onChange={(e) => set("home", "hero_title", e.target.value)} className={INPUT} /></Field>
                        <Field label="Hero Alt Başlık"><input data-testid="hero-subtitle" value={data.home?.hero_subtitle || ""} onChange={(e) => set("home", "hero_subtitle", e.target.value)} className={INPUT} /></Field>
                        <Field label="Destinasyonlar Başlığı"><input value={data.home?.destinations_title || ""} onChange={(e) => set("home", "destinations_title", e.target.value)} className={INPUT} /></Field>
                        <Field label="Öne Çıkanlar Başlığı"><input value={data.home?.featured_title || ""} onChange={(e) => set("home", "featured_title", e.target.value)} className={INPUT} /></Field>
                        <Field label="Fırsatlar Başlığı"><input value={data.home?.deals_title || ""} onChange={(e) => set("home", "deals_title", e.target.value)} className={INPUT} /></Field>
                    </div>
                    <div className="mt-4">
                        <ImageUpload testId="hero-image" label="Hero Arka Plan Resmi" value={data.home?.hero_image_url} onChange={(url) => set("home", "hero_image_url", url)} />
                    </div>
                    {/* Banners */}
                    <div className="mt-6">
                        <div className="flex items-center justify-between mb-2">
                            <Label>Slider / Banner Görselleri</Label>
                            <button data-testid="add-banner" onClick={() => set("home", "banners", [...(data.home?.banners || []), { id: uid("b"), image_url: "", title: "", subtitle: "", cta_label: "", cta_url: "", order: (data.home?.banners?.length || 0) + 1, active: true }])} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold"><Plus className="h-3.5 w-3.5" /> Banner Ekle</button>
                        </div>
                        <div className="space-y-2">
                            {(data.home?.banners || []).map((b) => (
                                <div key={b.id} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 grid grid-cols-12 gap-2 items-start">
                                    <div className="col-span-3"><ImageUpload testId={`banner-${b.id}`} label="" small value={b.image_url} onChange={(url) => set("home", "banners", (data.home?.banners || []).map((x) => x.id === b.id ? { ...x, image_url: url } : x))} /></div>
                                    <div className="col-span-3 space-y-2">
                                        <input placeholder="Başlık" value={b.title} onChange={(e) => set("home", "banners", (data.home?.banners || []).map((x) => x.id === b.id ? { ...x, title: e.target.value } : x))} className={INPUT} />
                                        <input placeholder="Alt başlık" value={b.subtitle} onChange={(e) => set("home", "banners", (data.home?.banners || []).map((x) => x.id === b.id ? { ...x, subtitle: e.target.value } : x))} className={INPUT} />
                                    </div>
                                    <div className="col-span-3 space-y-2">
                                        <input placeholder="Buton metni" value={b.cta_label} onChange={(e) => set("home", "banners", (data.home?.banners || []).map((x) => x.id === b.id ? { ...x, cta_label: e.target.value } : x))} className={INPUT} />
                                        <input placeholder="Buton URL" value={b.cta_url} onChange={(e) => set("home", "banners", (data.home?.banners || []).map((x) => x.id === b.id ? { ...x, cta_url: e.target.value } : x))} className={INPUT} />
                                    </div>
                                    <div className="col-span-3 flex items-start justify-end gap-2 pt-2">
                                        <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={!!b.active} onChange={(e) => set("home", "banners", (data.home?.banners || []).map((x) => x.id === b.id ? { ...x, active: e.target.checked } : x))} className="h-3.5 w-3.5 accent-sky-500" /> Aktif</label>
                                        <button onClick={() => set("home", "banners", (data.home?.banners || []).filter((x) => x.id !== b.id))} className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </div>
                            ))}
                            {!(data.home?.banners || []).length && <div className="text-xs italic text-slate-400 py-4">Henüz banner eklenmedi.</div>}
                        </div>
                    </div>
                </Section>
            )}

            {tab === "pages" && (
                <Section title="Statik Sayfalar"
                    action={<button data-testid="add-page" onClick={() => {
                        const newPage = { id: uid("p"), slug: `sayfa-${Date.now()}`, title: "Yeni Sayfa", content_html: "<p></p>", active: true, order: (data.pages?.length || 0) + 1, show_in_footer: false };
                        setSection("pages", [...(data.pages || []), newPage]);
                        setEditingPage(newPage.id);
                    }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold"><Plus className="h-3.5 w-3.5" /> Yeni Sayfa</button>}
                >
                    {!editingPage ? (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                <tr><th className="px-3 py-2 text-left">Başlık</th><th className="px-3 py-2 text-left">Slug</th><th className="px-3 py-2 text-center">{"Footer'da"}</th><th className="px-3 py-2 text-center">Durum</th><th className="px-3 py-2 text-right">İşlem</th></tr>
                            </thead>
                            <tbody>
                                {(data.pages || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((p) => (
                                    <tr key={p.id} className="border-t border-slate-100 dark:border-slate-800/50">
                                        <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{p.title}</td>
                                        <td className="px-3 py-2 font-mono text-[11px] text-slate-500">/page/{p.slug}</td>
                                        <td className="px-3 py-2 text-center"><input type="checkbox" checked={!!p.show_in_footer} onChange={(e) => setSection("pages", (data.pages || []).map((x) => x.id === p.id ? { ...x, show_in_footer: e.target.checked } : x))} className="h-3.5 w-3.5 accent-sky-500" /></td>
                                        <td className="px-3 py-2 text-center"><label className="inline-flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={!!p.active} onChange={(e) => setSection("pages", (data.pages || []).map((x) => x.id === p.id ? { ...x, active: e.target.checked } : x))} className="h-3.5 w-3.5 accent-emerald-500" />{p.active ? "Aktif" : "Pasif"}</label></td>
                                        <td className="px-3 py-2 text-right">
                                            <button data-testid={`edit-page-${p.id}`} onClick={() => setEditingPage(p.id)} className="px-2 py-1 rounded text-xs font-semibold text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/40">Düzenle</button>
                                            <button onClick={() => { if (window.confirm("Sayfayı sil?")) setSection("pages", (data.pages || []).filter((x) => x.id !== p.id)); }} className="px-2 py-1 rounded text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30">Sil</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (() => {
                        const p = (data.pages || []).find((x) => x.id === editingPage);
                        if (!p) { setEditingPage(null); return null; }
                        const upd = (patch) => setSection("pages", (data.pages || []).map((x) => x.id === p.id ? { ...x, ...patch } : x));
                        return (
                            <div className="space-y-3">
                                <button onClick={() => setEditingPage(null)} className="text-sm text-slate-600 hover:text-sky-600 font-semibold">← Listeye dön</button>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <Field label="Başlık"><input data-testid="page-title" value={p.title} onChange={(e) => upd({ title: e.target.value })} className={INPUT} /></Field>
                                    <Field label="Slug (URL)"><input data-testid="page-slug" value={p.slug} onChange={(e) => upd({ slug: slugify(e.target.value) })} className={`${INPUT} font-mono`} /></Field>
                                    <Field label="Footer'da Göster"><label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"><input type="checkbox" checked={!!p.show_in_footer} onChange={(e) => upd({ show_in_footer: e.target.checked })} className="h-4 w-4 accent-sky-500" /><span className="text-sm">Aktif</span></label></Field>
                                </div>
                                <Field label="İçerik">
                                    <RichTextEditor value={p.content_html} onChange={(html) => upd({ content_html: html })} testId="page-editor" />
                                </Field>
                            </div>
                        );
                    })()}
                </Section>
            )}

            {tab === "footer" && (
                <Section title="Footer">
                    {/* Columns */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label>Sütunlar</Label>
                            <button data-testid="add-footer-col" onClick={() => set("footer", "columns", [...(data.footer?.columns || []), { id: uid("c"), title: "Yeni Sütun", links: [] }])} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold"><Plus className="h-3.5 w-3.5" /> Sütun Ekle</button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {(data.footer?.columns || []).map((c) => (
                                <div key={c.id} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <input data-testid={`footer-col-title-${c.id}`} value={c.title} onChange={(e) => set("footer", "columns", (data.footer?.columns || []).map((x) => x.id === c.id ? { ...x, title: e.target.value } : x))} placeholder="Sütun başlığı" className={`${INPUT} font-bold`} />
                                        <button onClick={() => set("footer", "columns", (data.footer?.columns || []).filter((x) => x.id !== c.id))} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                    {(c.links || []).map((l) => (
                                        <div key={l.id} className="grid grid-cols-12 gap-1.5 items-center">
                                            <input value={l.label} onChange={(e) => set("footer", "columns", (data.footer?.columns || []).map((x) => x.id === c.id ? { ...x, links: x.links.map((y) => y.id === l.id ? { ...y, label: e.target.value } : y) } : x))} placeholder="Etiket" className={`${INPUT} col-span-5 text-xs`} />
                                            <input value={l.url} onChange={(e) => set("footer", "columns", (data.footer?.columns || []).map((x) => x.id === c.id ? { ...x, links: x.links.map((y) => y.id === l.id ? { ...y, url: e.target.value } : y) } : x))} placeholder="URL" className={`${INPUT} col-span-6 text-xs`} />
                                            <button onClick={() => set("footer", "columns", (data.footer?.columns || []).map((x) => x.id === c.id ? { ...x, links: x.links.filter((y) => y.id !== l.id) } : x))} className="col-span-1 p-1 rounded text-red-600"><Trash2 className="h-3 w-3" /></button>
                                        </div>
                                    ))}
                                    <button onClick={() => set("footer", "columns", (data.footer?.columns || []).map((x) => x.id === c.id ? { ...x, links: [...x.links, { id: uid("l"), label: "", url: "" }] } : x))} className="w-full text-xs font-semibold text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/30 rounded py-1.5">+ Link Ekle</button>
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Social */}
                    <div className="mt-6">
                        <Label>Sosyal Medya</Label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {["facebook", "instagram", "twitter", "tiktok", "whatsapp", "youtube"].map((k) => (
                                <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
                                    <input data-testid={`social-${k}`} value={(data.footer?.social || {})[k] || ""} onChange={(e) => set("footer", "social", { ...(data.footer?.social || {}), [k]: e.target.value })} placeholder="https://..." className={INPUT} />
                                </Field>
                            ))}
                        </div>
                    </div>
                    {/* Contact */}
                    <div className="mt-6">
                        <Label>İletişim</Label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Field label="Telefon"><input value={(data.footer?.contact || {}).phone || ""} onChange={(e) => set("footer", "contact", { ...(data.footer?.contact || {}), phone: e.target.value })} className={INPUT} /></Field>
                            <Field label="E-posta"><input value={(data.footer?.contact || {}).email || ""} onChange={(e) => set("footer", "contact", { ...(data.footer?.contact || {}), email: e.target.value })} className={INPUT} /></Field>
                            <Field label="Adres"><input value={(data.footer?.contact || {}).address || ""} onChange={(e) => set("footer", "contact", { ...(data.footer?.contact || {}), address: e.target.value })} className={INPUT} /></Field>
                        </div>
                    </div>

                    {/* Certifications (ETBİS / TÜRSAB) */}
                    <div className="mt-6">
                        <Label>Sertifikalar & Yasal Rozetler</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/40">
                                <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-2">ETBİS — Elektronik Ticaret Bilgi Sistemi</div>
                                <ImageUpload
                                    testId="etbis-logo"
                                    label="ETBİS Logo"
                                    value={(data.footer?.certifications || {}).etbis_logo_url || ""}
                                    onChange={(url) => set("footer", "certifications", { ...(data.footer?.certifications || {}), etbis_logo_url: url })}
                                    small
                                />
                                <div className="mt-2">
                                    <Field label="ETBİS No"><input data-testid="etbis-number" value={(data.footer?.certifications || {}).etbis_number || ""} onChange={(e) => set("footer", "certifications", { ...(data.footer?.certifications || {}), etbis_number: e.target.value })} placeholder="örn: 12345678" className={INPUT} /></Field>
                                </div>
                                <div className="mt-2">
                                    <Field label="ETBİS Doğrulama Linki (opsiyonel)"><input data-testid="etbis-link" value={(data.footer?.certifications || {}).etbis_link || ""} onChange={(e) => set("footer", "certifications", { ...(data.footer?.certifications || {}), etbis_link: e.target.value })} placeholder="https://www.eticaret.gov.tr/siteprofil/..." className={INPUT} /></Field>
                                </div>
                            </div>
                            <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/40">
                                <div className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-2">TÜRSAB — Türkiye Seyahat Acenteleri Birliği</div>
                                <ImageUpload
                                    testId="tursab-logo"
                                    label="TÜRSAB Logo"
                                    value={(data.footer?.certifications || {}).tursab_logo_url || ""}
                                    onChange={(url) => set("footer", "certifications", { ...(data.footer?.certifications || {}), tursab_logo_url: url })}
                                    small
                                />
                                <div className="mt-2">
                                    <Field label="TÜRSAB Belge No"><input data-testid="tursab-number" value={(data.footer?.certifications || {}).tursab_number || ""} onChange={(e) => set("footer", "certifications", { ...(data.footer?.certifications || {}), tursab_number: e.target.value })} placeholder="örn: A-1234" className={INPUT} /></Field>
                                </div>
                                <div className="mt-2">
                                    <Field label="TÜRSAB Doğrulama Linki (opsiyonel)"><input data-testid="tursab-link" value={(data.footer?.certifications || {}).tursab_link || ""} onChange={(e) => set("footer", "certifications", { ...(data.footer?.certifications || {}), tursab_link: e.target.value })} placeholder="https://www.tursab.org.tr/..." className={INPUT} /></Field>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment methods */}
                    <div className="mt-6">
                        <Label>Ödeme Yöntemleri</Label>
                        <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-800/40 space-y-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    data-testid="payment-show-cards"
                                    checked={(data.footer?.payment_methods?.show_card_logos) !== false}
                                    onChange={(e) => set("footer", "payment_methods", { ...(data.footer?.payment_methods || {}), show_card_logos: e.target.checked })}
                                    className="h-4 w-4 accent-sky-500"
                                />
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Visa / Mastercard / Troy logolarını göster</span>
                            </label>
                            <div className="text-xs text-slate-500">{"Standart kart logoları otomatik olarak footer'da gösterilir. Aşağıdan ekstra logo (iyzico, PayTR, BKM Express vb.) ekleyebilirsiniz."}</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                                {(data.footer?.payment_methods?.extra_logos || []).map((logoUrl, i) => (
                                    <div key={i} className="space-y-1">
                                        <ImageUpload
                                            testId={`payment-extra-${i}`}
                                            value={logoUrl}
                                            onChange={(url) => {
                                                const list = [...(data.footer?.payment_methods?.extra_logos || [])];
                                                list[i] = url;
                                                set("footer", "payment_methods", { ...(data.footer?.payment_methods || {}), extra_logos: list });
                                            }}
                                            small
                                        />
                                        <button type="button" onClick={() => {
                                            const list = (data.footer?.payment_methods?.extra_logos || []).filter((_, j) => j !== i);
                                            set("footer", "payment_methods", { ...(data.footer?.payment_methods || {}), extra_logos: list });
                                        }} className="w-full text-xs font-semibold text-red-600 hover:underline">Sil</button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    data-testid="payment-add-extra"
                                    onClick={() => set("footer", "payment_methods", { ...(data.footer?.payment_methods || {}), extra_logos: [...(data.footer?.payment_methods?.extra_logos || []), ""] })}
                                    className="h-20 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-slate-400 hover:border-sky-400 hover:text-sky-500 text-xs font-semibold flex items-center justify-center transition-colors"
                                >
                                    + Logo Ekle
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Copyright */}
                    <div className="mt-6">
                        <Field label="Telif Metni"><input data-testid="footer-copyright" value={data.footer?.copyright || ""} onChange={(e) => set("footer", "copyright", e.target.value)} className={INPUT} /></Field>
                    </div>
                </Section>
            )}

            {tab === "seo" && (
                <Section title="SEO & Meta">
                    <div className="space-y-3">
                        <Field label="Site Title (Browser tab)"><input data-testid="seo-title" value={data.seo?.title || ""} onChange={(e) => set("seo", "title", e.target.value)} className={INPUT} /></Field>
                        <Field label="Meta Description"><textarea data-testid="seo-description" rows={3} value={data.seo?.description || ""} onChange={(e) => set("seo", "description", e.target.value)} className={INPUT} /></Field>
                        <Field label="Anahtar Kelimeler (virgülle ayırın)"><input data-testid="seo-keywords" value={data.seo?.keywords || ""} onChange={(e) => set("seo", "keywords", e.target.value)} className={INPUT} /></Field>
                        <ImageUpload testId="og-image" label="Open Graph Görseli (sosyal paylaşımda)" value={data.seo?.og_image_url} onChange={(url) => set("seo", "og_image_url", url)} />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Google Analytics ID (GA-...)"><input data-testid="seo-ga" value={data.seo?.ga_id || ""} onChange={(e) => set("seo", "ga_id", e.target.value)} placeholder="G-XXXXXXXXXX" className={INPUT} /></Field>
                            <Field label="Google Tag Manager ID (GTM-...)"><input data-testid="seo-gtm" value={data.seo?.gtm_id || ""} onChange={(e) => set("seo", "gtm_id", e.target.value)} placeholder="GTM-XXXXXX" className={INPUT} /></Field>
                        </div>
                    </div>
                </Section>
            )}

            {tab === "seo_tools" && (
                <SeoToolsTab data={data} set={set} setSection={setSection} />
            )}
        </AdminLayout>
    );
}

function moveItem(data, section, setSection, id, dir) {
    const arr = [...(data[section] || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = arr.findIndex((x) => x.id === id);
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx].order, arr[target].order] = [arr[target].order, arr[idx].order];
    setSection(section, arr);
}

function Section({ title, children, action }) {
    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">{title}</h2>
                {action}
            </div>
            {children}
        </div>
    );
}

function Label({ children }) { return <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">{children}</div>; }
function Field({ label, children }) { return <label className="block">{label && <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>}{children}</label>; }

function ImageUpload({ testId, label, value, onChange, small = false }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const r = await api.post("/admin/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(r.data.url);
        } catch (e) {
            alert(e.response?.data?.detail || "Yükleme başarısız");
        } finally { setUploading(false); }
    };
    return (
        <div data-testid={`upload-${testId}`}>
            {label && <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>}
            <div className={`relative rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 ${small ? "h-20" : "h-32"} flex items-center justify-center overflow-hidden`}>
                {value ? (
                    <img src={resolveImageUrl(value)} alt="" className="w-full h-full object-contain p-2" />
                ) : (
                    <div className="text-center text-xs text-slate-400 px-2">
                        <Upload className="h-4 w-4 mx-auto mb-1" />
                        Resim yükle
                    </div>
                )}
                <input ref={inputRef} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="absolute inset-0 opacity-0 cursor-pointer" />
            </div>
            <div className="mt-1 flex items-center gap-2">
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="text-xs font-semibold text-sky-600 hover:underline disabled:opacity-50">{uploading ? "Yükleniyor..." : (value ? "Değiştir" : "Yükle")}</button>
                {value && <button type="button" onClick={() => onChange("")} className="text-xs font-semibold text-red-600 hover:underline">Kaldır</button>}
            </div>
        </div>
    );
}


// =========================================================================
// FAZ 1 — Theme Tab (colors, fonts, radius, shadow, dark-mode default)
// =========================================================================
const PRESETS = [
    { id: "sky", label: "Sky (Varsayılan)", primary: "#0ea5e9", primary_hover: "#0284c7", accent: "#f59e0b" },
    { id: "emerald", label: "Doğa", primary: "#10b981", primary_hover: "#059669", accent: "#f97316" },
    { id: "rose", label: "Premium", primary: "#e11d48", primary_hover: "#be123c", accent: "#fbbf24" },
    { id: "indigo", label: "Kurumsal", primary: "#4f46e5", primary_hover: "#4338ca", accent: "#f43f5e" },
    { id: "amber", label: "Sıcak", primary: "#d97706", primary_hover: "#b45309", accent: "#0ea5e9" },
    { id: "slate", label: "Minimal", primary: "#1e293b", primary_hover: "#0f172a", accent: "#0ea5e9" },
];
const FONTS = ["Inter", "Poppins", "Roboto", "Montserrat", "Open Sans", "Lato", "Nunito", "Raleway", "Playfair Display", "Manrope", "Outfit", "Plus Jakarta Sans", "DM Sans", "Quicksand", "Work Sans"];

function ThemeTab({ data, set }) {
    const t = data.theme || {};
    const applyPreset = (p) => {
        set("theme", "primary", p.primary);
        set("theme", "primary_hover", p.primary_hover);
        set("theme", "accent", p.accent);
    };
    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Tema & Renkler</h2>
                <span className="text-xs text-slate-500">Değişiklikler kaydedildikten sonra tüm site genelinde uygulanır.</span>
            </div>

            {/* Presets */}
            <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Hazır Paletler</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    {PRESETS.map((p) => (
                        <button key={p.id} type="button" data-testid={`preset-${p.id}`} onClick={() => applyPreset(p)}
                            className="p-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 transition-colors text-left">
                            <div className="flex gap-1 mb-2">
                                <div className="h-6 flex-1 rounded" style={{ background: p.primary }} />
                                <div className="h-6 flex-1 rounded" style={{ background: p.primary_hover }} />
                                <div className="h-6 flex-1 rounded" style={{ background: p.accent }} />
                            </div>
                            <div className="text-xs font-semibold">{p.label}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Color pickers */}
            <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Özel Renkler</div>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <ColorField label="Birincil" testId="primary" value={t.primary} onChange={(v) => set("theme", "primary", v)} />
                    <ColorField label="Birincil Hover" testId="primary-hover" value={t.primary_hover} onChange={(v) => set("theme", "primary_hover", v)} />
                    <ColorField label="Vurgu" testId="accent" value={t.accent} onChange={(v) => set("theme", "accent", v)} />
                    <ColorField label="Başarı" testId="success" value={t.success} onChange={(v) => set("theme", "success", v)} />
                    <ColorField label="Hata" testId="danger" value={t.danger} onChange={(v) => set("theme", "danger", v)} />
                </div>
            </div>

            {/* Typography */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Gövde Yazı Tipi</span>
                    <select data-testid="font-body" value={t.font_family || "Inter"} onChange={(e) => set("theme", "font_family", e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                        {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Başlık Yazı Tipi</span>
                    <select data-testid="font-display" value={t.font_display || "Poppins"} onChange={(e) => set("theme", "font_display", e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                        {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                </label>
            </div>

            {/* Radius + Shadow */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Köşe Yuvarlaklığı</span>
                    <select data-testid="radius" value={t.radius || "1rem"} onChange={(e) => set("theme", "radius", e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                        <option value="0">Köşeli (0)</option>
                        <option value="0.375rem">Az (6px)</option>
                        <option value="0.75rem">Orta (12px)</option>
                        <option value="1rem">Yumuşak (16px)</option>
                        <option value="1.5rem">Çok Yumuşak (24px)</option>
                    </select>
                </label>
                <label className="block">
                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Gölge Yoğunluğu</span>
                    <select data-testid="shadow" value={t.shadow_intensity || "md"} onChange={(e) => set("theme", "shadow_intensity", e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                        <option value="sm">Hafif</option>
                        <option value="md">Orta</option>
                        <option value="lg">Belirgin</option>
                    </select>
                </label>
                <label className="flex items-center gap-2 mt-6 cursor-pointer">
                    <input type="checkbox" data-testid="dark-default" checked={!!t.dark_mode_default} onChange={(e) => set("theme", "dark_mode_default", e.target.checked)} className="h-4 w-4 accent-sky-500" />
                    <span className="text-sm font-medium">Koyu temayı varsayılan yap</span>
                </label>
            </div>

            {/* Live preview */}
            <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Canlı Önizleme</div>
                <div className="p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 space-y-3"
                    style={{ fontFamily: `'${t.font_family || "Inter"}', system-ui` }}>
                    <h3 className="text-2xl font-bold" style={{ fontFamily: `'${t.font_display || "Poppins"}', system-ui`, color: t.primary }}>Hayalindeki Tatili Keşfet</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{"Türkiye'nin en seçkin otellerinde rezervasyon yapın."}</p>
                    <div className="flex gap-2 flex-wrap">
                        <button type="button" style={{ background: t.primary, borderRadius: t.radius }} className="px-4 py-2 text-white text-sm font-semibold">Rezervasyon Yap</button>
                        <button type="button" style={{ background: t.accent, borderRadius: t.radius }} className="px-4 py-2 text-white text-sm font-semibold">Detayları Gör</button>
                        <button type="button" style={{ background: t.success, borderRadius: t.radius }} className="px-4 py-2 text-white text-sm font-semibold">Onaylandı</button>
                        <button type="button" style={{ background: t.danger, borderRadius: t.radius }} className="px-4 py-2 text-white text-sm font-semibold">İptal</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ColorField({ label, testId, value, onChange }) {
    return (
        <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
            <div className="flex items-center gap-2 p-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                <input type="color" data-testid={`color-${testId}`} value={value || "#0ea5e9"} onChange={(e) => onChange(e.target.value)} className="h-8 w-10 rounded cursor-pointer border-0 bg-transparent" />
                <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 text-xs font-mono bg-transparent outline-none uppercase" placeholder="#0ea5e9" />
            </div>
        </label>
    );
}

// =========================================================================
// FAZ 1 — Home Sections Composer (drag-up/down + enable toggle)
// =========================================================================
const SECTION_LABELS = {
    hero: { label: "Hero & Arama", desc: "Büyük başlık, arka plan ve arama widget'ı" },
    trust_bar: { label: "Güven Bandı", desc: "Güvenli ödeme, anında onay vb." },
    destinations: { label: "Popüler Destinasyonlar", desc: "Bento grid: Antalya, Bodrum, vb." },
    featured_hotels: { label: "Öne Çıkan Oteller", desc: "Story avatars + kart grid" },
    deals: { label: "Sıcak Fırsatlar", desc: "Mavi banner + indirimli oteller" },
    reviews: { label: "Müşteri Yorumları", desc: "3'lü testimonial bandı" },
};

/** Mini SVG thumbnail previews — purely visual; gives admin instant context */
function SectionThumb({ type }) {
    const common = "w-24 h-16 rounded-lg flex-shrink-0 overflow-hidden border border-slate-200 dark:border-slate-700";
    if (type === "hero") return (
        <div className={`${common} bg-gradient-to-br from-sky-400 to-sky-700 relative`}>
            <div className="absolute top-2 left-2 right-2 h-1.5 bg-white/80 rounded" />
            <div className="absolute top-5 left-2 w-12 h-1 bg-white/60 rounded" />
            <div className="absolute bottom-2 left-2 right-2 h-3 bg-white rounded-md" />
        </div>
    );
    if (type === "trust_bar") return (
        <div className={`${common} bg-slate-100 dark:bg-slate-800 grid grid-cols-4 gap-1 p-1.5`}>
            {[0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-white dark:bg-slate-700 rounded flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-sky-500" />
                </div>
            ))}
        </div>
    );
    if (type === "destinations") return (
        <div className={`${common} grid grid-cols-3 gap-1 p-1 bg-slate-50 dark:bg-slate-800`}>
            <div className="row-span-2 bg-gradient-to-br from-amber-400 to-rose-500 rounded" />
            <div className="bg-gradient-to-br from-emerald-400 to-teal-600 rounded" />
            <div className="bg-gradient-to-br from-purple-400 to-indigo-600 rounded" />
            <div className="bg-gradient-to-br from-sky-400 to-blue-600 rounded" />
            <div className="bg-gradient-to-br from-orange-400 to-red-500 rounded" />
        </div>
    );
    if (type === "featured_hotels") return (
        <div className={`${common} bg-white dark:bg-slate-800 p-1.5 flex flex-col gap-1`}>
            <div className="flex gap-1">
                {[0, 1, 2, 3, 4].map((i) => <div key={i} className="w-3 h-3 rounded-full bg-gradient-to-br from-pink-400 to-fuchsia-600" />)}
            </div>
            <div className="grid grid-cols-4 gap-0.5 flex-1">
                {[0, 1, 2, 3].map((i) => <div key={i} className="bg-slate-200 dark:bg-slate-700 rounded" />)}
            </div>
        </div>
    );
    if (type === "deals") return (
        <div className={`${common} bg-gradient-to-br from-sky-500 to-sky-700 p-1.5 flex flex-col gap-1`}>
            <div className="h-1 bg-amber-300 rounded w-2/3" />
            <div className="h-1.5 bg-white/80 rounded w-full" />
            <div className="grid grid-cols-3 gap-0.5 flex-1 mt-0.5">
                {[0, 1, 2].map((i) => <div key={i} className="bg-white/40 rounded" />)}
            </div>
        </div>
    );
    if (type === "reviews") return (
        <div className={`${common} bg-white dark:bg-slate-800 p-1.5 grid grid-cols-3 gap-1`}>
            {[0, 1, 2].map((i) => (
                <div key={i} className="bg-slate-100 dark:bg-slate-700 rounded p-1 flex flex-col gap-0.5">
                    <div className="flex gap-0.5">{[0, 1, 2].map((s) => <div key={s} className="w-0.5 h-0.5 rounded-full bg-amber-400" />)}</div>
                    <div className="flex-1 bg-slate-200 dark:bg-slate-600 rounded-sm" />
                </div>
            ))}
        </div>
    );
    return <div className={`${common} bg-slate-100 dark:bg-slate-800`} />;
}

function HomeSectionsTab({ data, setSection }) {
    const sections = (data.home_sections || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const move = (id, dir) => {
        const arr = [...sections];
        const idx = arr.findIndex((s) => s.id === id);
        const target = idx + dir;
        if (target < 0 || target >= arr.length) return;
        [arr[idx], arr[target]] = [arr[target], arr[idx]];
        // Re-number orders
        arr.forEach((s, i) => { s.order = i + 1; });
        setSection("home_sections", arr);
    };
    const toggle = (id) => {
        setSection("home_sections", sections.map((s) => s.id === id ? { ...s, enabled: !s.enabled } : s));
    };
    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-4">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Anasayfa Düzeni</h2>
                    <p className="text-xs text-slate-500 mt-1">Bölümleri sürükleyerek yukarı/aşağı taşıyın veya açıp kapatın. Anasayfanın görünümü buradaki sıraya göre üretilir.</p>
                </div>
            </div>
            <div className="space-y-2" data-testid="home-sections-list">
                {sections.map((s, idx) => {
                    const meta = SECTION_LABELS[s.type] || { label: s.type, desc: "" };
                    return (
                        <div key={s.id} data-testid={`hs-${s.type}`}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${s.enabled ? "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30" : "border-dashed border-slate-200 dark:border-slate-700 bg-transparent opacity-60"}`}>
                            <div className="flex flex-col gap-1">
                                <button type="button" onClick={() => move(s.id, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                                <button type="button" onClick={() => move(s.id, +1)} disabled={idx === sections.length - 1} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                            </div>
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-950/40 text-sky-600 flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                            <SectionThumb type={s.type} />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-slate-900 dark:text-white">{meta.label}</div>
                                <div className="text-xs text-slate-500 truncate">{meta.desc}</div>
                            </div>
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" data-testid={`hs-toggle-${s.type}`} checked={!!s.enabled} onChange={() => toggle(s.id)} className="h-4 w-4 accent-emerald-500" />
                                <span className="text-xs font-semibold">{s.enabled ? "Açık" : "Kapalı"}</span>
                            </label>
                        </div>
                    );
                })}
            </div>
            <div className="text-[11px] italic text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-800">
                {`💡 İpucu: "Hero & Arama" bölümünü kapatırsanız anasayfada arama widget'ı görünmez. Bu yalnızca özel kampanya sayfaları için önerilir.`}
            </div>
        </div>
    );
}

// =========================================================================
// FAZ 1 — Multi-level Menu Builder (drop-down children)
// =========================================================================
function MultiLevelMenuTab({ data, setSection }) {
    const [expanded, setExpanded] = useState({});
    const menu = (data.header_menu || []);
    const setMenu = (next) => setSection("header_menu", next);
    const addRoot = () => {
        setMenu([...menu, { id: uid("m"), label: "Yeni Menü", url: "/", target: "_self", active: true, order: menu.length + 1, children: [] }]);
    };
    const addChild = (parentId) => {
        setMenu(menu.map((m) => m.id === parentId ? { ...m, children: [...(m.children || []), { id: uid("c"), label: "Yeni Alt Öğe", url: "/", target: "_self", active: true }] } : m));
        setExpanded((p) => ({ ...p, [parentId]: true }));
    };
    const updRoot = (id, patch) => setMenu(menu.map((m) => m.id === id ? { ...m, ...patch } : m));
    const delRoot = (id) => setMenu(menu.filter((m) => m.id !== id));
    const moveRoot = (id, dir) => {
        const arr = menu.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const idx = arr.findIndex((m) => m.id === id);
        const t = idx + dir;
        if (t < 0 || t >= arr.length) return;
        [arr[idx].order, arr[t].order] = [arr[t].order, arr[idx].order];
        setMenu(arr);
    };
    const updChild = (parentId, childId, patch) => {
        setMenu(menu.map((m) => m.id === parentId ? { ...m, children: (m.children || []).map((c) => c.id === childId ? { ...c, ...patch } : c) } : m));
    };
    const delChild = (parentId, childId) => {
        setMenu(menu.map((m) => m.id === parentId ? { ...m, children: (m.children || []).filter((c) => c.id !== childId) } : m));
    };

    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Üst Menü (Çok Seviyeli)</h2>
                <button data-testid="add-menu-item" onClick={addRoot} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold"><Plus className="h-3.5 w-3.5" /> Ana Menü Ekle</button>
            </div>
            <div className="space-y-2" data-testid="menu-list">
                {menu.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((m, idx, arr) => {
                    const hasKids = (m.children || []).length > 0;
                    const isOpen = expanded[m.id] !== false; // default open
                    return (
                        <div key={m.id} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                            <div data-testid={`menu-row-${m.id}`} className="grid grid-cols-12 gap-2 items-center p-3 bg-slate-50 dark:bg-slate-800/40">
                                <button type="button" onClick={() => setExpanded((p) => ({ ...p, [m.id]: !isOpen }))} className="col-span-1 flex items-center justify-center p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
                                    {hasKids ? (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />) : <div className="h-4 w-4 opacity-20">·</div>}
                                </button>
                                <div className="col-span-3"><input data-testid={`menu-label-${m.id}`} value={m.label} onChange={(e) => updRoot(m.id, { label: e.target.value })} placeholder="Etiket" className={INPUT} /></div>
                                <div className="col-span-3"><input data-testid={`menu-url-${m.id}`} value={m.url} onChange={(e) => updRoot(m.id, { url: e.target.value })} placeholder="/path veya https://..." className={INPUT} /></div>
                                <div className="col-span-2">
                                    <select value={m.target} onChange={(e) => updRoot(m.id, { target: e.target.value })} className={INPUT}>
                                        <option value="_self">Aynı Sekme</option>
                                        <option value="_blank">Yeni Sekme</option>
                                    </select>
                                </div>
                                <div className="col-span-3 flex items-center justify-end gap-1">
                                    <button type="button" data-testid={`add-child-${m.id}`} onClick={() => addChild(m.id)} title="Alt öğe ekle" className="p-1 rounded hover:bg-emerald-50 dark:hover:bg-emerald-950/30 text-emerald-600"><Plus className="h-3.5 w-3.5" /></button>
                                    <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={!!m.active} onChange={(e) => updRoot(m.id, { active: e.target.checked })} className="h-3.5 w-3.5 accent-sky-500" />Aktif</label>
                                    <button onClick={() => moveRoot(m.id, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                                    <button onClick={() => moveRoot(m.id, +1)} disabled={idx === arr.length - 1} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                                    <button data-testid={`del-menu-${m.id}`} onClick={() => delRoot(m.id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            </div>
                            {hasKids && isOpen && (
                                <div className="bg-white dark:bg-slate-900 px-3 py-2 space-y-1.5 border-t border-slate-200 dark:border-slate-700">
                                    {(m.children || []).map((c) => (
                                        <div key={c.id} data-testid={`child-row-${c.id}`} className="grid grid-cols-12 gap-2 items-center pl-8">
                                            <div className="col-span-4"><input value={c.label} onChange={(e) => updChild(m.id, c.id, { label: e.target.value })} placeholder="Alt etiket" className={INPUT} /></div>
                                            <div className="col-span-4"><input value={c.url} onChange={(e) => updChild(m.id, c.id, { url: e.target.value })} placeholder="/path" className={INPUT} /></div>
                                            <div className="col-span-2">
                                                <select value={c.target || "_self"} onChange={(e) => updChild(m.id, c.id, { target: e.target.value })} className={INPUT}>
                                                    <option value="_self">Aynı</option>
                                                    <option value="_blank">Yeni</option>
                                                </select>
                                            </div>
                                            <div className="col-span-2 flex items-center justify-end gap-1">
                                                <label className="flex items-center gap-1 text-xs cursor-pointer"><input type="checkbox" checked={c.active !== false} onChange={(e) => updChild(m.id, c.id, { active: e.target.checked })} className="h-3 w-3 accent-sky-500" />Aktif</label>
                                                <button data-testid={`del-child-${c.id}`} onClick={() => delChild(m.id, c.id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="h-3 w-3" /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                {menu.length === 0 && <div className="text-xs italic text-slate-400 py-4 text-center">{`Henüz menü öğesi yok. "Ana Menü Ekle" ile başlayın.`}</div>}
            </div>
            <div className="text-[11px] italic text-slate-400 mt-3">
                {`💡 İpucu: Ana menüye "+" butonu ile alt öğe ekleyerek mega-dropdown menüler kurabilirsiniz (örn. Destinasyonlar → Antalya / Bodrum / Kapadokya).`}
            </div>
        </div>
    );
}


// =========================================================================
// FAZ 3 — Promo Strip Tab (top announcement bar)
// =========================================================================
function PromoStripTab({ data, set }) {
    const p = data.promo_strip || {};
    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Üst Duyuru Bandı</h2>
                    <p className="text-xs text-slate-500 mt-1">Navbar'ın üzerinde tüm sitede görünecek küçük bant. Kampanya, duyuru ya da kupon kodu paylaşmak için ideal.</p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" data-testid="promo-enabled" checked={!!p.enabled} onChange={(e) => set("promo_strip", "enabled", e.target.checked)} className="h-4 w-4 accent-emerald-500" />
                    <span className="text-sm font-bold">{p.enabled ? "Açık" : "Kapalı"}</span>
                </label>
            </div>
            <Field label="Mesaj"><input data-testid="promo-message" value={p.message || ""} onChange={(e) => set("promo_strip", "message", e.target.value)} className={INPUT} placeholder="🎁 Yaz kampanyası: 5 gece üzeri rezervasyonlarda %15 indirim" /></Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Link metni (ops.)"><input value={p.link_label || ""} onChange={(e) => set("promo_strip", "link_label", e.target.value)} className={INPUT} /></Field>
                <Field label="Link URL"><input value={p.link_url || ""} onChange={(e) => set("promo_strip", "link_url", e.target.value)} className={INPUT} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Arka plan rengi"><input type="color" value={p.bg_color || "#0ea5e9"} onChange={(e) => set("promo_strip", "bg_color", e.target.value)} className="h-10 w-full rounded-xl" /></Field>
                <Field label="Yazı rengi"><input type="color" value={p.text_color || "#ffffff"} onChange={(e) => set("promo_strip", "text_color", e.target.value)} className="h-10 w-full rounded-xl" /></Field>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={p.dismissible !== false} onChange={(e) => set("promo_strip", "dismissible", e.target.checked)} className="h-4 w-4 accent-sky-500" />
                <span className="text-sm">Ziyaretçi kapatabilsin (X butonu)</span>
            </label>
            {/* Preview */}
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 pt-3 border-t border-slate-100 dark:border-slate-800">Önizleme</div>
            <div className="rounded-xl overflow-hidden" style={{ background: p.bg_color || "#0ea5e9", color: p.text_color || "#fff" }}>
                <div className="px-4 py-2 text-sm text-center font-medium">
                    {p.message || "Mesaj giriniz..."}
                    {p.link_label && <span className="underline font-bold ml-2">{p.link_label} →</span>}
                </div>
            </div>
        </div>
    );
}

// =========================================================================
// FAZ 3 — Hotel Detail Tail Blocks (CMS blocks appended to every hotel page)
// =========================================================================
function HotelTailBlocksTab({ data, setSection }) {
    const blocks = data.hotel_detail_tail_blocks || [];
    const BLOCK_TYPES = ["text", "cta", "faq", "hotel_list", "embed_video", "spacer"];
    const BLOCK_LABELS = { text: "Metin", cta: "CTA / Çağrı", faq: "SSS", hotel_list: "Otel Listesi", embed_video: "Video", spacer: "Boşluk" };

    const addBlock = (type) => {
        const defaults = {
            text: { content_html: "<h3>Başlık</h3><p>Buraya metin girin...</p>" },
            cta: { title: "Hayalindeki tatile başla", primary_label: "Rezervasyon Yap", primary_url: "/search", bg_color: "linear-gradient(135deg, #0ea5e9, #0369a1)" },
            faq: { title: "Sıkça Sorulan Sorular", items: [{ question: "Soru?", answer: "Cevap..." }] },
            hotel_list: { title: "Yakındaki Oteller", source: "featured", limit: 3, columns: 3 },
            embed_video: { youtube_url: "" },
            spacer: { height: "40px" },
        };
        const newBlock = { id: `b_${Math.random().toString(36).slice(2, 10)}`, type, props: defaults[type] || {} };
        setSection("hotel_detail_tail_blocks", [...blocks, newBlock]);
    };
    const removeBlock = (id) => setSection("hotel_detail_tail_blocks", blocks.filter((b) => b.id !== id));
    const moveBlock = (id, dir) => {
        const arr = [...blocks];
        const idx = arr.findIndex((b) => b.id === id);
        const t = idx + dir;
        if (t < 0 || t >= arr.length) return;
        [arr[idx], arr[t]] = [arr[t], arr[idx]];
        setSection("hotel_detail_tail_blocks", arr);
    };

    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-4">
            <div>
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Otel Sayfası Alt Eki</h2>
                <p className="text-xs text-slate-500 mt-1">Tüm otel detay sayfalarının altına otomatik eklenen bloklar. Cross-sell, SSS, güven mesajı veya yakındaki otelleri burada gösterin.</p>
                <p className="text-[11px] italic text-amber-600 mt-1">💡 Detaylı blok ayarları için tab üstündeki <strong>Kaydet</strong>'i kullandıktan sonra blok props'larını CMS Sayfası editöründekine benzer şekilde ileride genişleteceğiz. Şimdilik varsayılan içerikle eklenir, ihtiyaca göre site_content JSON üzerinden ayarlanabilir.</p>
            </div>
            <div className="space-y-2" data-testid="tail-blocks-list">
                {blocks.length === 0 && <div className="text-xs italic text-slate-400 py-4 text-center">Henüz blok yok. Aşağıdan ekleyin.</div>}
                {blocks.map((b, idx, arr) => (
                    <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
                        <div className="flex flex-col gap-0.5">
                            <button onClick={() => moveBlock(b.id, -1)} disabled={idx === 0} className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
                            <button onClick={() => moveBlock(b.id, +1)} disabled={idx === arr.length - 1} className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-950/40 text-sky-600 flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                        <div className="flex-1">
                            <div className="font-semibold text-sm">{BLOCK_LABELS[b.type] || b.type}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{b.id}</div>
                        </div>
                        <button onClick={() => removeBlock(b.id)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                ))}
            </div>
            <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">+ Yeni Blok</div>
                <div className="flex flex-wrap gap-1.5">
                    {BLOCK_TYPES.map((t) => (
                        <button key={t} data-testid={`add-tail-${t}`} onClick={() => addBlock(t)} className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-sky-100 dark:hover:bg-sky-950/40 text-xs font-semibold">{BLOCK_LABELS[t]}</button>
                    ))}
                </div>
            </div>
        </div>
    );
}

// =========================================================================
// FAZ 4 — SEO Tools Tab (robots.txt + sitemap configuration)
// =========================================================================
function SeoToolsTab({ data, set, setSection }) {
    const rt = data.robots_txt || {};
    const sm = data.sitemap || {};
    const setRt = (k, v) => set("robots_txt", k, v);
    const setSm = (k, v) => set("sitemap", k, v);
    const apiBase = (typeof window !== "undefined" && process.env.REACT_APP_BACKEND_URL) || "";

    return (
        <div className="space-y-4">
            {/* Robots.txt */}
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3" data-testid="seo-robots">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">robots.txt</h2>
                    {apiBase && <a href={`${apiBase}/api/robots.txt`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-sky-600 hover:underline">Mevcut robots.txt'i görüntüle ↗</a>}
                </div>
                <p className="text-xs text-slate-500">Arama motorlarına hangi yolları taramayacaklarını söyleyen dosya. Boş bırakırsanız sistem varsayılan kullanır.</p>
                <Field label="Özel robots.txt içeriği (boş = otomatik)">
                    <textarea data-testid="robots-custom" rows={8} value={rt.custom_content || ""} onChange={(e) => setRt("custom_content", e.target.value)} placeholder={`User-agent: *\nAllow: /\nDisallow: /admin/\nDisallow: /h/\nSitemap: https://...sitemap.xml`} className={`${INPUT} font-mono text-xs`} />
                </Field>
                <Field label="Veya — sadece ek Disallow yolları ekle (her satıra bir tane)">
                    <textarea
                        data-testid="robots-extra"
                        rows={3}
                        value={(rt.extra_disallow || []).join("\n")}
                        onChange={(e) => setRt("extra_disallow", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                        placeholder={`/private-area\n/admin-test`}
                        className={`${INPUT} font-mono text-xs`}
                    />
                </Field>
            </div>

            {/* Sitemap */}
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3" data-testid="seo-sitemap">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">sitemap.xml</h2>
                    {apiBase && <a href={`${apiBase}/api/sitemap.xml`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-sky-600 hover:underline">Mevcut sitemap.xml'i görüntüle ↗</a>}
                </div>
                <p className="text-xs text-slate-500">Hangi sayfa türlerinin Google sitemap'inde yer alacağını seçin.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                        { k: "include_static", label: "Statik sayfalar (Anasayfa, Hakkımızda...)" },
                        { k: "include_destinations", label: "Destinasyon sayfaları" },
                        { k: "include_hotels", label: "Otel detay sayfaları" },
                        { k: "include_cms_pages", label: "CMS sayfaları (Yayında olanlar)" },
                    ].map((opt) => (
                        <label key={opt.k} className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 cursor-pointer">
                            <input type="checkbox" data-testid={`sm-${opt.k}`} checked={sm[opt.k] !== false} onChange={(e) => setSm(opt.k, e.target.checked)} className="h-4 w-4 accent-sky-500" />
                            <span className="text-sm font-medium">{opt.label}</span>
                        </label>
                    ))}
                </div>
            </div>
        </div>
    );
}

